import { useEffect, useRef, useState, KeyboardEvent } from 'react';
import { Box, SxProps, useTheme } from '@mui/material';
import Image from 'next/image';
import GradientBackground from './GradientBackground';
import useIsFocused from './useIsFocused';
import Thumbnails from './Thumbnails';
import { useSwipeable } from 'react-swipeable';
import Arrow from './Arrow';
import OptionalViewport from '../../OptionalViewport';
import Optional from '../../Optional';

type Props = {
  images: {
    url: string;
    thumbnailUrl: string;
    blurUrl?: string;
    alt?: string;
  }[];
  sx?: SxProps;
  isMobile?: boolean;
};

const StandardGallery = ({ images, sx, isMobile }: Props) => {
  const theme = useTheme();
  const [selected, setSelected] = useState(0);

  const ref = useRef<HTMLDivElement>(null);
  const isFocused = useIsFocused(ref);

  const next = () => {
    const max = images.length - 1;
    setSelected((prev) => {
      if (max === prev) return 0;
      return prev + 1;
    });
  };

  const back = () => {
    const max = images.length - 1;
    setSelected((prev) => {
      if (0 === prev) return max;
      return prev - 1;
    });
  };

  const handlers = useSwipeable({
    trackMouse: true,
    preventScrollOnSwipe: true,
    onSwipedLeft: next,
    onSwipedRight: back,
  });

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!isFocused) return;
    if (event.key === 'ArrowLeft') {
      back();
    } else if (event.key === 'ArrowRight') {
      next();
    }
  };

  useEffect(() => {
    setSelected(0);
  }, [images]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
      }}
      {...handlers}
    >
      <Box
        ref={ref}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 1,
          ...sx,
        }}
      >
        {images.map(({ url, blurUrl, alt }, index) => {
          const isNext =
            (selected === images.length - 1 ? 0 : selected + 1) === index;
          const isPrev =
            (selected === 0 ? images.length - 1 : selected - 1) === index;
          const isSelected = selected === index;
          const shouldPriorityLoad = isSelected || isNext || isPrev;
          return (
            <Box
              key={url + index}
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: isSelected ? 2 : 1,
                borderRadius: 'inherit',
                opacity: isSelected ? 1 : 0,
                transition: theme.transitions.create('opacity', {
                  duration: 300,
                }),
              }}
            >
              <Image
                key={url + index}
                src={url}
                alt={alt || `Gallery image ${index}`}
                fill
                sizes={'50vw'}
                placeholder={blurUrl ? 'blur' : 'empty'}
                blurDataURL={blurUrl}
                loading={shouldPriorityLoad ? 'eager' : 'lazy'}
                style={{
                  borderRadius: 'inherit',
                  objectFit: 'cover',
                  userSelect: 'none',
                }}
              />
            </Box>
          );
        })}
        <Thumbnails
          initIsMobile={isMobile}
          images={images}
          selected={selected}
          onClick={setSelected}
        />
        <OptionalViewport viewport={'desktop'} condition={images.length > 1}>
          <Arrow side={'left'} onClick={back} />
          <Arrow side={'right'} onClick={next} />
        </OptionalViewport>
        <Optional condition={images.length > 1}>
          <GradientBackground />
        </Optional>
      </Box>
    </Box>
  );
};

export default StandardGallery;
